import React, { FunctionComponent, useState } from 'react';
import { Modal } from '@vwfs-bronson/bronson-react';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getInitialValues } from './initialValues';
import { MockSearchCustomerFormModel, MockSearchCustomerItem } from './formModel';
import { CustomerSearchModalForm } from './CustomerSearchModalForm';
import { CustomerSearchModalTable } from './CustomerSearchModalTable';
import { mockSearchCustomerAPI } from './mockSearchCustomerAPI';
import { uiStateActions } from '../../../../../services/redux/features/uiState.slice';

interface CustomerSearchModalProps {
  onCustomerSelected: (customer: MockSearchCustomerItem | null) => void;
}

interface CustomerSearchModalContentProps {
  modalInstanceKey: number;
  hasSearched: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  handlePageChange: (page: number) => void;
  lastSearchValues: MockSearchCustomerFormModel | null;
  searchResults: MockSearchCustomerItem[];
  selectedCustomerId: string | null;
  setSelectedCustomerId: React.Dispatch<React.SetStateAction<string | null>>;
  onResetSearch: (selectedCustomer?: MockSearchCustomerItem) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

type CustomerSearchModalState = {
  uiState: {
    shownCustomerSearchModal: boolean;
  };
};

const CustomerSearchModalContent: FunctionComponent<CustomerSearchModalContentProps> = ({
  modalInstanceKey,
  currentPage,
  totalPages,
  totalItems,
  handlePageChange,
  hasSearched,
  lastSearchValues,
  searchResults,
  selectedCustomerId,
  setSelectedCustomerId,
  onResetSearch,
  t,
}) => {
  const { submitForm, resetForm } = useFormikContext<MockSearchCustomerFormModel>();

  const dispatch = useDispatch();
  const isShown = useSelector((state: CustomerSearchModalState) => state.uiState.shownCustomerSearchModal);

  const handleCloseModal = () => {
    resetForm();
    dispatch(uiStateActions.setShownCustomerSearchModal(false));
    onResetSearch();
  };

  const handleConfirm = () => {
    const hasSearchResults = searchResults.length > 0;
    const isContinueMode = hasSearched && hasSearchResults;

    if (!isContinueMode) {
      // eslint-disable-next-line no-void
      void submitForm();
      return;
    }

    const selectedCustomer = searchResults.find((item) => item.id === selectedCustomerId);
    if (!selectedCustomer) return;

    resetForm();
    onResetSearch(selectedCustomer);
  };

  const hasSearchResults = searchResults.length > 0;
  const isContinueMode = hasSearched && hasSearchResults;
  const confirmLabel = isContinueMode
    ? t('customerSearchModal:business:buttons:continue')
    : t('customerSearchModal:business:buttons:search');
  const isConfirmDisabled = isContinueMode && !selectedCustomerId;

  return (
    <Modal
      key={modalInstanceKey}
      large
      className="c-modal--large"
      buttonCloseLabel={t('customerSearchModal:business:buttons:close')}
      buttonCancelLabel={t('customerSearchModal:business:buttons:back')}
      buttonCancelText={t('customerSearchModal:business:buttons:back')}
      buttonConfirmLabel={confirmLabel}
      confirmationDisabled={isConfirmDisabled}
      buttonConfirmText={confirmLabel}
      buttonConfirmType="button"
      id="CustomerSearchModal"
      onCancel={handleCloseModal}
      onConfirm={handleConfirm}
      onClose={handleCloseModal}
      shown={isShown}
      title={t('customerSearchModal:business:title')}
    >
      {/* minHeight to avoid dropdown opening to the top */}
      <div style={{ minHeight: '50vh' }}>
        <CustomerSearchModalForm />
        {hasSearched && (
          <CustomerSearchModalTable
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            handlePageChange={handlePageChange}
            lastSearchValues={lastSearchValues}
            searchResults={searchResults}
            hasSearched={hasSearched}
            selectedCustomerId={selectedCustomerId}
            setSelectedCustomerId={setSelectedCustomerId}
            t={t}
          />
        )}
      </div>
    </Modal>
  );
};

export const CustomerSearchModal: FunctionComponent<CustomerSearchModalProps> = ({ onCustomerSelected }) => {
  const { t } = useTranslation();

  const [initialValues] = useState(getInitialValues);
  const [modalInstanceKey, setModalInstanceKey] = useState(0);
  const [searchResults, setSearchResults] = useState<MockSearchCustomerItem[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [lastSearchValues, setLastSearchValues] = useState<MockSearchCustomerFormModel | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const loadSearchResults = async (values: MockSearchCustomerFormModel, page: number) => {
    const response = await mockSearchCustomerAPI({
      ...values,
      page,
    });

    setCurrentPage(response.pagination.currentPage);
    setTotalPages(response.pagination.totalPages);
    setTotalItems(response.pagination.totalItems);

    setSearchResults(response.items ?? []);
  };

  const onSubmit = async (
    values: MockSearchCustomerFormModel,
    { setSubmitting }: FormikHelpers<MockSearchCustomerFormModel>
  ) => {
    try {
      setSubmitting(true);
      setHasSearched(true);

      setLastSearchValues(values);
      setSelectedCustomerId(null);
      await loadSearchResults(values, 1);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Customer search failed:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageChange = (page: number) => {
    if (!lastSearchValues) return;

    setSelectedCustomerId(null);
    // eslint-disable-next-line no-void
    void loadSearchResults(lastSearchValues, page);
  };

  const resetSearchState = () => {
    setCurrentPage(1);
    setTotalPages(1);
    setTotalItems(0);
    setHasSearched(false);
    setLastSearchValues(null);
    setSearchResults([]);
    setSelectedCustomerId(null);
    setModalInstanceKey((previous) => previous + 1);
  };

  const resetCustomerSearch = (selectedCustomer?: MockSearchCustomerItem) => {
    resetSearchState();

    if (selectedCustomer) {
      onCustomerSelected(selectedCustomer);
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validateOnMount>
      <CustomerSearchModalContent
        modalInstanceKey={modalInstanceKey}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        handlePageChange={handlePageChange}
        hasSearched={hasSearched}
        lastSearchValues={lastSearchValues}
        searchResults={searchResults}
        selectedCustomerId={selectedCustomerId}
        setSelectedCustomerId={setSelectedCustomerId}
        onResetSearch={resetCustomerSearch}
        t={t}
      />
    </Formik>
  );
};
